import {
  Regulation,
  StandstillExemption,
  type SentStatutoryClausesDto,
} from "../../../autogen/bff-api";
import { displayOsloDate } from "../../../common/formatting/displayOsloDate";

// Standard Norwegian award-letter (tildelingsbrev) boilerplate. These are formal procurement-law
// texts rendered verbatim in the letters, so they are kept as Norwegian constants rather than i18n keys.

const senderOrFallback = (senderName?: string | null): string =>
  senderName?.trim() || "oppdragsgiver";

const contestChoice = (juryOnlyAdvises: boolean): string =>
  juryOnlyAdvises ? "oppdragsgiver, etter juryens vurdering, har valgt" : "juryen har kåret";

export interface JuryRankingSkips {
  rejected: { name: string; place: number }[];
  winnerCount: number;
}

const skipsRejected = (skips?: JuryRankingSkips): skips is JuryRankingSkips => (skips?.rejected.length ?? 0) > 0;

const skippedNote = ({ rejected, winnerCount }: JuryRankingSkips): string => {
  const entries = winnerNamesText(rejected.map(({ name, place }) => `bidraget fra ${name.trim() || "en deltaker"} som nr. ${place}`));
  const pronoun = rejected.length === 1 ? "det" : "de";
  const winners =
    winnerCount === 1
      ? "Vinneren er derfor det høyest rangerte bidraget som ikke er avvist."
      : "Vinnerne er derfor de høyest rangerte bidragene som ikke er avvist.";
  return `\n\nJuryen rangerte ${entries}, men ${pronoun} er avvist. ${winners}`;
};

const contestWinnerSentence = (
  subject: string,
  juryOnlyAdvises: boolean,
  skips: JuryRankingSkips | undefined,
): string =>
  !juryOnlyAdvises && skipsRejected(skips)
    ? `${subject} er vinner.${skippedNote(skips)}`
    : `${contestChoice(juryOnlyAdvises)} ${subject} som vinner.`;

const prizeSentence = (prizes: string[]): string =>
  prizes.length === 0
    ? ""
    : prizes.length === 1
      ? `\n\nPremien er ${prizes[0]}.`
      : `\n\nPremiene i konkurransen er ${winnerNamesText(prizes)}.`;

export const winnerIntro = (
  eventTitle: string,
  senderName?: string | null,
  designContest = false,
  juryOnlyAdvises = false,
  designContestPrizes: string[] = [],
  skips?: JuryRankingSkips,
): string =>
  designContest
    ? `Vi viser til Deres bidrag i plan- og designkonkurransen ${eventTitle}.\n\n` +
      `På vegne av ${senderOrFallback(senderName)} meddeles herved at ` +
      contestWinnerSentence("Deres bidrag", juryOnlyAdvises, skips) +
      prizeSentence(designContestPrizes)
    : `Vi viser til Deres tilbud i konkurransen om ${eventTitle}.\n\n` +
      `På vegne av ${senderOrFallback(senderName)} meddeles herved at kontrakten vil bli inngått med Deres firma.`;

export const rejectionIntro = (
  eventTitle: string,
  winnerName: string,
  senderName?: string | null,
  designContest = false,
  juryOnlyAdvises = false,
  skips?: JuryRankingSkips,
): string =>
  designContest
    ? `Vi viser til Deres bidrag i plan- og designkonkurransen ${eventTitle}.\n\n` +
      `På vegne av ${senderOrFallback(senderName)} meddeles herved at ` +
      contestWinnerSentence(otherEntry(winnerName), juryOnlyAdvises, skips)
    : `Vi viser til Deres tilbud i konkurransen om ${eventTitle}.\n\n` +
      `På vegne av ${senderOrFallback(senderName)} meddeles herved at kontrakten vil bli inngått med ` +
      `${winnerName.trim() || "en annen leverandør"}.`;

export const rejectedTenderIntro = (
  eventTitle: string,
  winnerName: string,
  senderName?: string | null,
  designContest = false,
  juryOnlyAdvises = false,
  skips?: JuryRankingSkips,
): string =>
  designContest
    ? `Vi viser til Deres bidrag i plan- og designkonkurransen ${eventTitle}, som er avvist. ` +
      `Begrunnelsen for avvisningen står i meldingen om avvisning.\n\n` +
      `På vegne av ${senderOrFallback(senderName)} meddeles herved at ` +
      contestWinnerSentence(otherEntry(winnerName), juryOnlyAdvises, skips)
    : `Vi viser til Deres tilbud i konkurransen om ${eventTitle}, som er avvist. ` +
      `Begrunnelsen for avvisningen står i meldingen om avvisning.\n\n` +
      `På vegne av ${senderOrFallback(senderName)} meddeles herved at kontrakten vil bli inngått med ` +
      `${winnerName.trim() || "en annen leverandør"}.`;

const otherEntry = (winnerName: string): string =>
  winnerName.trim() ? `bidraget fra ${winnerName.trim()}` : "et annet bidrag";

export const letterIntro = ({
  eventTitle,
  winnerName,
  senderName,
  clauseContext,
  isRejection,
  forRejectedTender,
}: {
  eventTitle: string;
  winnerName: string;
  senderName?: string | null;
  clauseContext: StatutoryClauseContext;
  isRejection: boolean;
  forRejectedTender: boolean;
}): string => {
  const { designContest, juryOnlyAdvises, designContestPrizes, juryRankingSkips } = clauseContext;
  if (forRejectedTender) {
    return rejectedTenderIntro(eventTitle, winnerName, senderName, designContest, juryOnlyAdvises, juryRankingSkips);
  }
  if (isRejection) {
    return rejectionIntro(eventTitle, winnerName, senderName, designContest, juryOnlyAdvises, juryRankingSkips);
  }
  return winnerIntro(eventTitle, senderName, designContest, juryOnlyAdvises, designContestPrizes, juryRankingSkips);
};

export const signOff = (
  senderName?: string | null,
  signerName?: string | null,
): string =>
  ["Med vennlig hilsen,", signerName?.trim(), senderOrFallback(senderName)]
    .filter(Boolean)
    .join("\n");

export const winnerNamesText = (names: string[]): string =>
  names.length <= 1
    ? (names[0] ?? "")
    : `${names.slice(0, -1).join(", ")} og ${names[names.length - 1]}`;

export interface StandstillTerms {
  buyerSetsStandstill: boolean;
  days?: number | null;
  exemption?: StandstillExemption | null;
  rejectedInFavourOf?: string | null;
  expiresAt?: string | null;
  awaitsDispatch?: boolean;
  voluntary?: boolean;
  withoutProvision?: boolean;
  underPartIV?: boolean;
  underDps?: boolean;
  utilitiesPartIII?: boolean;
}

const soleConcernedSupplier = (rejectedInFavourOf: string | null | undefined, provision: string): string =>
  rejectedInFavourOf?.trim()
    ? `Det er ingen karensperiode fordi ${rejectedInFavourOf.trim()} er den eneste berørte leverandøren etter at ` +
      `de øvrige tilbudene er avvist (${provision}).`
    : `Det er ingen karensperiode fordi dere er den eneste berørte leverandøren (${provision}).`;

const NO_STANDSTILL: Record<
  StandstillExemption.FrameworkAgreementCallOff,
  string
> = {
  [StandstillExemption.FrameworkAgreementCallOff]:
    "Det er ingen karensperiode fordi kontrakten tildeles under en rammeavtale " +
    "(FOA § 10-2 tredje ledd bokstav c).",
};

const noticeExemption = (regulation: Regulation | null | undefined, utilitiesPartIII = false): string => {
  if (utilitiesPartIII) return "forsyningsforskriften § 26-8 annet ledd";
  return regulation === Regulation.Forsyningsforskriften
    ? "forsyningsforskriften § 21-2 annet ledd bokstav a"
    : "FOA § 25-2 annet ledd bokstav a";
};

const withoutPriorNotice = (regulation: Regulation | null | undefined, utilitiesPartIII = false): string =>
  "Det er ingen karensperiode fordi kontrakten inngås uten forutgående kunngjøring " +
  `(${noticeExemption(regulation, utilitiesPartIII)}).`;

const CONTRACT_WAITS =
  "Kontrakten vil ikke bli inngått før karensperioden er utløpt.";

const expirySentence = ({
  expiresAt,
  awaitsDispatch,
}: StandstillTerms): string | null => {
  if (expiresAt)
    return `Karensperioden utløper ${displayOsloDate(expiresAt, "nb")}.`;
  if (awaitsDispatch)
    return "Datoen karensperioden utløper, fastsettes når meddelelsen sendes.";
  return null;
};

const statutoryKarensInfo = (
  provision: string,
  standstill: StandstillTerms,
): string => {
  const { days } = standstill;
  const period =
    days == null
      ? "en karensperiode"
      : `en karensperiode på ${days} ${days === 1 ? "dag" : "dager"}`;
  return [
    `Det gjelder ${period} regnet fra dagen etter at denne meddelelsen er sendt, jf. ${provision}.`,
    expirySentence(standstill),
    CONTRACT_WAITS,
  ]
    .filter(Boolean)
    .join(" ");
};

const voluntaryKarensInfo = (
  regulation: Regulation | null | undefined,
  standstill: StandstillTerms,
): string => {
  const days = standstill.days ?? 0;
  return [
    `Oppdragsgiveren har valgt å praktisere en karensperiode på ${days} ${days === 1 ? "dag" : "dager"} regnet fra ` +
      "dagen etter at denne meddelelsen er sendt, selv om karensperiode ikke er påkrevd " +
      `(${noticeExemption(regulation, standstill.utilitiesPartIII)}).`,
    expirySentence(standstill),
    CONTRACT_WAITS,
  ]
    .filter(Boolean)
    .join(" ");
};

const buyerSetKarensInfo = (regulation: Regulation | null | undefined, standstill: StandstillTerms): string => {
  const { exemption, rejectedInFavourOf } = standstill;
  if (standstill.underPartIV && regulation === Regulation.Forsyningsforskriften) {
    return buyerSetUnderUtilitiesPartIII(standstill);
  }
  if (standstill.underPartIV) return buyerSetUnderPartIV(standstill);
  if (exemption === StandstillExemption.SoleConcernedSupplier)
    return soleConcernedSupplier(rejectedInFavourOf, "FOA § 10-2 tredje ledd bokstav b");
  if (exemption) return NO_STANDSTILL[exemption];
  return statutoryKarensInfo("anskaffelsesforskriften § 10-2", standstill);
};

const buyerSetUnderUtilitiesPartIII = (standstill: StandstillTerms): string => {
  if (standstill.exemption === StandstillExemption.SoleConcernedSupplier)
    return soleConcernedSupplier(standstill.rejectedInFavourOf, "forsyningsforskriften § 26-8 annet ledd");
  if (standstill.exemption === StandstillExemption.FrameworkAgreementCallOff)
    return "Det er ingen karensperiode fordi kontrakten tildeles under en rammeavtale.";
  return statutoryKarensInfo("forsyningsforskriften § 26-8", standstill);
};

const buyerSetUnderPartIV = (standstill: StandstillTerms): string => {
  if (standstill.exemption === StandstillExemption.SoleConcernedSupplier)
    return soleConcernedSupplier(standstill.rejectedInFavourOf, "FOA § 30-9 annet ledd");
  if (standstill.exemption === StandstillExemption.FrameworkAgreementCallOff)
    return "Det er ingen karensperiode fordi kontrakten tildeles under en rammeavtale.";
  return statutoryKarensInfo("anskaffelsesforskriften § 30-9", standstill);
};

const underDynamicPurchasingSystem = (regulation: Regulation | null | undefined): string => {
  const provision =
    regulation === Regulation.Forsyningsforskriften
      ? "forsyningsforskriften § 21-2 annet ledd bokstav d"
      : "FOA § 25-2 annet ledd bokstav d";
  return `Det er ingen karensperiode fordi kontrakten tildeles under en dynamisk innkjøpsordning (${provision}).`;
};

export const karensInfo = (
  regulation: Regulation | null | undefined,
  standstill?: StandstillTerms,
): string => {
  if (standstill?.underDps) return underDynamicPurchasingSystem(regulation);
  if (standstill?.voluntary) {
    return (standstill.days ?? 0) > 0
      ? voluntaryKarensInfo(regulation, standstill)
      : withoutPriorNotice(regulation, standstill.utilitiesPartIII);
  }
  if (standstill?.buyerSetsStandstill) return buyerSetKarensInfo(regulation, standstill);
  const provision =
    regulation === Regulation.Forsyningsforskriften
      ? "forsyningsforskriften § 21-2"
      : "anskaffelsesforskriften § 25-2";
  return statutoryKarensInfo(
    provision,
    standstill ?? { buyerSetsStandstill: false },
  );
};

export const noStandstillProvision = (
  regulation: Regulation | null | undefined,
  standstill: StandstillTerms,
): string | null => {
  const { exemption } = standstill;
  if (!exemption || standstill.withoutProvision) return null;
  const sole = exemption === StandstillExemption.SoleConcernedSupplier;
  if (standstill.underPartIV && regulation === Regulation.Forsyningsforskriften) {
    return sole ? "forsyningsforskriften § 26-8 annet ledd" : null;
  }
  if (standstill.underPartIV) return sole ? "FOA § 30-9 annet ledd" : null;
  return sole ? "FOA § 10-2 tredje ledd bokstav b" : "FOA § 10-2 tredje ledd bokstav c";
};

export const hasStandstillPeriod = (standstill?: StandstillTerms): boolean => {
  if (standstill?.underDps || standstill?.withoutProvision) return false;
  if (standstill?.voluntary) return (standstill.days ?? 0) > 0;
  if (standstill?.buyerSetsStandstill) return !standstill.exemption;
  return true;
};

export const KLAGE_INFO =
  "Eventuell klage på tildelingsbeslutningen kan rettes til oppdragsgiver eller til Klagenemnda for " +
  "offentlige anskaffelser (KOFA), Postboks 511 Sentrum, 5805 Bergen, eller per e-post post@kofa.no.";

const CONTEST_KLAGE_INFO =
  "Eventuell klage på utfallet av konkurransen kan rettes til oppdragsgiver eller til Klagenemnda for " +
  "offentlige anskaffelser (KOFA), Postboks 511 Sentrum, 5805 Bergen, eller per e-post post@kofa.no.";

const CONTEST_NO_STANDSTILL = "Plan- og designkonkurransen tildeler ingen kontrakt, så det gjelder ingen karensperiode.";

export interface StatutoryClauseContext {
  eventRunsPrivateFlow: boolean;
  buyerRunsPublicProcurements: boolean;
  outsideProcurementAct?: boolean;
  regulation: Regulation | null | undefined;
  standstill?: StandstillTerms;
  designContest?: boolean;
  juryOnlyAdvises?: boolean;
  designContestPrizes?: string[];
  juryRankingSkips?: JuryRankingSkips;
}

// FOA § 25-2 standstill never applies to the private flow (private buyer or FOA Part I). KOFA hears only
// public buyers (LOA § 7a), and nothing under 500 000 kr (LOA § 2, klagenemndforskriften § 6).
export const statutoryClauses = ({
  eventRunsPrivateFlow,
  buyerRunsPublicProcurements,
  outsideProcurementAct = false,
  regulation,
  standstill,
  designContest = false,
}: StatutoryClauseContext): { standstill?: string; complaint?: string } => ({
  ...(eventRunsPrivateFlow || standstill?.withoutProvision
    ? {}
    : { standstill: designContest ? CONTEST_NO_STANDSTILL : karensInfo(regulation, standstill) }),
  ...(buyerRunsPublicProcurements && !outsideProcurementAct
    ? { complaint: designContest ? CONTEST_KLAGE_INFO : KLAGE_INFO }
    : {}),
});

export const letterPromisesStandstill = (context: StatutoryClauseContext): boolean =>
  !context.eventRunsPrivateFlow && !context.designContest && hasStandstillPeriod(context.standstill);

export const clausesAsShown = (
  context: StatutoryClauseContext,
  sentClauses?: SentStatutoryClausesDto | null,
): { standstill?: string; complaint?: string } =>
  sentClauses
    ? {
        standstill: sentClauses.standstill ?? undefined,
        complaint: sentClauses.complaint ?? undefined,
      }
    : statutoryClauses(context);

export const warnsAgainstNamingWinner = (
  isRejection: boolean,
  namesWinnerToOtherBidders: boolean,
): boolean => isRejection && !namesWinnerToOtherBidders;

export const withdrawnStandstill = (
  buyerSetsStandstill: boolean,
  voluntary: boolean,
  decisionDays: number | null | undefined,
): StandstillTerms =>
  voluntary
    ? { buyerSetsStandstill, voluntary, days: decisionDays }
    : { buyerSetsStandstill };

export const forRecipient = (
  context: StatutoryClauseContext,
  rejectedInFavourOf: string | null,
): StatutoryClauseContext =>
  context.standstill
    ? { ...context, standstill: { ...context.standstill, rejectedInFavourOf } }
    : context;
